// 3rd party
import * as React from "react";
import {
	BrowserRouter,
	Switch,
	Route,
	RouteComponentProps,
	Redirect,
} from "react-router-dom";
import * as miimetiqUI from "miimetiq-ui";
import * as reduxLoop from "redux-loop";
import * as reactRedux from "react-redux";
import { Either, Maybe } from "tsmonad";
import * as immutable from "immutable";
import * as jsonous from "jsonous";

// Domain
import * as domain from "@src/domain";

// Persistence
import * as persistence from "@src/persistence";
import { applyJsonousDecoder } from "@src/persistence/decoding";

// Toolkits
import * as architecture from "@src/presentation/toolkit/architecture";

// Views
import { Homepage } from "@src/presentation/homepage";

// Styles
import * as styles from "./styles";

export type { State };
export { ConnectedView, reducer, init };
// MODEL

interface Uninitialized {
	initialization: "uninitialized";
}

interface Initialized {
	config: domain.Config;
	user: domain.User;
	productInfo: domain.ProductInfo;
	productQuantities: domain.ProductQuantities;
	initialization: "success";
	expandedGroups: immutable.Set<domain.Group["name"]>;
	timerId: Maybe<number>;
}
interface Pending {
	initialization: "pending";
}

interface Failed {
	initialization: "failed";
	errorMsg: string;
}

type State = Uninitialized | Initialized | Pending | Failed;

const init: Uninitialized = {
	initialization: "uninitialized",
};

// UPDATE

type Action =
	| {
			type: "INITIALIZATION";
			step: "START";
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "CONFIGURE";
			status: "START";
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "CONFIGURE";
			status: "SUCCESS";
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "REGISTER AS IFRAME PARENT";
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "ENSURE VALID SESSION";
			status: "START";
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "ENSURE VALID SESSION";
			status: "SUCCESS";
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "LOAD USER";
			status: "START";
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "LOAD USER";
			status: "SUCCESS";
			user: domain.User;
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "LOAD PRODUCT INFO";
			status: "START";
			user: domain.User;
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "LOAD PRODUCT INFO";
			status: "SUCCESS";
			user: domain.User;
			config: domain.Config;
			productInfo: domain.ProductInfo;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "LOAD PRODUCT QUANTITIES";
			status: "START";
			user: domain.User;
			config: domain.Config;
			productInfo: domain.ProductInfo;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "LOAD PRODUCT QUANTITIES";
			status: "SUCCESS";
			user: domain.User;
			config: domain.Config;
			productInfo: domain.ProductInfo;
			productQuantities: domain.ProductQuantities;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step: "FINISH";
			user: domain.User;
			config: domain.Config;
			productInfo: domain.ProductInfo;
			productQuantities: domain.ProductQuantities;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "INITIALIZATION";
			step:
				| "CONFIGURE"
				| "ENSURE VALID SESSION"
				| "LOAD USER"
				| "LOAD PRODUCT INFO";
			status: "FAILURE";
			errorMsg: string | undefined;
	  }
	| {
			type: "POLLING";
			step: "LOAD PRODUCT QUANTITIES";
			status: "FAILURE";
			errorMsg: string | undefined;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "LOGOUT";
			status: "START";
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "LOGOUT";
			status: "FAILURE";
			errorMsg: string;
	  }
	| {
			type: "LOGOUT";
			status: "SUCCESS";
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
	  }
	| {
			type: "TOGGLE GROUP EXPANSION";
			group: domain.Group;
	  }
	| PollingAction
	| {
			type: "NoOp";
	  };

type PollingAction =
	| {
			type: "SCHEDULE NEXT POLL";
			user: domain.User;
			config: domain.Config;
			dispatch: architecture.Dispatch<Action>;
			productInfo: domain.ProductInfo;
	  }
	| {
			type: "STORE POLL TIMER ID";
			timerId: number;
	  };

function reducer(state: State, action: Action): [State, reduxLoop.CmdType] {
	switch (action.type) {
		case "NoOp": {
			return [state, reduxLoop.Cmd.none];
		}
		case "INITIALIZATION": {
			switch (action.step) {
				case "START": {
					const newState: Pending = { initialization: "pending" };
					return [
						newState,
						reduxLoop.Cmd.action<Action>({
							type: "INITIALIZATION",
							step: "CONFIGURE",
							status: "START",
							dispatch: action.dispatch,
						}),
					];
				}
				case "CONFIGURE": {
					switch (action.status) {
						case "START": {
							const actionIfConfigureFails = (error: string): Action => ({
								type: "INITIALIZATION",
								step: "CONFIGURE",
								status: "FAILURE",
								errorMsg: error,
							});

							const actionIfConfigureSucceeds = (
								config: domain.Config
							): Action => {
								return {
									type: "INITIALIZATION",
									step: "CONFIGURE",
									status: "SUCCESS",
									config,
									dispatch: action.dispatch,
								};
							};
							const configureCmd = reduxLoop.Cmd.run(persistence.loadConfig, {
								successActionCreator: actionIfConfigureSucceeds,
								failActionCreator: actionIfConfigureFails,
							});
							return [state, configureCmd];
						}
						case "FAILURE": {
							const reason =
								action.errorMsg === undefined
									? "Unknown reason"
									: action.errorMsg;
							const errorMsg = `Could not configure the application: ${reason}`;
							const newState: State = {
								errorMsg,
								initialization: "failed",
							};
							return [newState, reduxLoop.Cmd.none];
						}
						case "SUCCESS": {
							return [
								state,
								reduxLoop.Cmd.action<Action>({
									type: "INITIALIZATION",
									step: "REGISTER AS IFRAME PARENT",
									status: "START",
									config: action.config,
									dispatch: action.dispatch,
								}),
							];
						}
						default: {
							return architecture.assertExhaustiveSwitch(action, [
								state,
								reduxLoop.Cmd.none,
							]);
						}
					}
				}
				case "REGISTER AS IFRAME PARENT": {
					registerAsIframeParent(action.config);
					return [
						state,
						reduxLoop.Cmd.action<Action>({
							type: "INITIALIZATION",
							step: "ENSURE VALID SESSION",
							status: "START",
							config: action.config,
							dispatch: action.dispatch,
						}),
					];
				}
				case "ENSURE VALID SESSION": {
					switch (action.status) {
						case "START": {
							const actionIfCmdSucceeds = (): Action => {
								return {
									type: "INITIALIZATION",
									step: "ENSURE VALID SESSION",
									status: "SUCCESS",
									config: action.config,
									dispatch: action.dispatch,
								};
							};

							const actionIfCmdFails = (error: string): Action => {
								return {
									type: "INITIALIZATION",
									step: "ENSURE VALID SESSION",
									status: "FAILURE",
									errorMsg: error,
								};
							};

							const ensureSessionCmd = reduxLoop.Cmd.run(ensureValidSession, {
								successActionCreator: actionIfCmdSucceeds,
								failActionCreator: actionIfCmdFails,
								args: [action.config],
							});
							return [state, ensureSessionCmd];
						}
						case "SUCCESS": {
							return [
								state,
								reduxLoop.Cmd.action<Action>({
									type: "INITIALIZATION",
									step: "LOAD USER",
									status: "START",
									config: action.config,
									dispatch: action.dispatch,
								}),
							];
						}
						case "FAILURE": {
							const reason =
								action.errorMsg === undefined
									? "Unknown reason"
									: action.errorMsg;
							const errorMsg = `Could not ensure a valid session. ${reason}`;
							return [
								state,
								reduxLoop.Cmd.action<Action>({
									type: "INITIALIZATION",
									status: "FAILURE",
									step: "ENSURE VALID SESSION",
									errorMsg,
								}),
							];
						}
						default: {
							return architecture.assertExhaustiveSwitch(action, [
								state,
								reduxLoop.Cmd.none,
							]);
						}
					}
				}
				case "LOAD USER": {
					switch (action.status) {
						case "START": {
							const actionIfCmdSucceeds = (user: domain.User): Action => {
								return {
									type: "INITIALIZATION",
									step: "LOAD USER",
									status: "SUCCESS",
									user,
									config: action.config,
									dispatch: action.dispatch,
								};
							};

							const actionIfCmdFails = (error: string): Action => {
								return {
									type: "INITIALIZATION",
									step: "LOAD USER",
									status: "FAILURE",
									errorMsg: error,
								};
							};
							const startGetUser = reduxLoop.Cmd.run(persistence.loadUser, {
								successActionCreator: actionIfCmdSucceeds,
								failActionCreator: actionIfCmdFails,
								args: [action.config],
							});
							return [state, startGetUser];
						}
						case "FAILURE": {
							const reason =
								action.errorMsg === undefined
									? "Unknown reason"
									: action.errorMsg;
							const errorMsg = `Could not load user: ${reason}`;
							const newState: State = {
								errorMsg,
								initialization: "failed",
							};
							return [newState, reduxLoop.Cmd.none];
						}
						case "SUCCESS": {
							return [
								state,
								reduxLoop.Cmd.action<Action>({
									type: "INITIALIZATION",
									step: "LOAD PRODUCT INFO",
									status: "START",
									user: action.user,
									config: action.config,
									dispatch: action.dispatch,
								}),
							];
						}
						default: {
							return architecture.assertExhaustiveSwitch(action, [
								state,
								reduxLoop.Cmd.none,
							]);
						}
					}
				}
				case "LOAD PRODUCT INFO": {
					switch (action.status) {
						case "START": {
							const actionIfCmdSucceeds = (
								info: domain.ProductInfo
							): Action => {
								return {
									type: "INITIALIZATION",
									step: "LOAD PRODUCT INFO",
									status: "SUCCESS",
									productInfo: info,
									user: action.user,
									config: action.config,
									dispatch: action.dispatch,
								};
							};

							const actionIfCmdFails = (error: string): Action => {
								return {
									type: "INITIALIZATION",
									step: "LOAD PRODUCT INFO",
									status: "FAILURE",
									errorMsg: error,
								};
							};

							const startGetInfo = reduxLoop.Cmd.run(
								persistence.loadProductInfo,
								{
									successActionCreator: actionIfCmdSucceeds,
									failActionCreator: actionIfCmdFails,
									args: [action.config],
								}
							);
							return [state, startGetInfo];
						}
						case "FAILURE": {
							const reason =
								action.errorMsg === undefined
									? "Unknown reason"
									: action.errorMsg;
							const errorMsg = `Could not load info: ${reason}`;
							const newState: State = {
								errorMsg,
								initialization: "failed",
							};
							return [newState, reduxLoop.Cmd.none];
						}
						case "SUCCESS": {
							return [
								state,
								reduxLoop.Cmd.action<Action>({
									type: "INITIALIZATION",
									step: "LOAD PRODUCT QUANTITIES",
									status: "START",
									user: action.user,
									config: action.config,
									productInfo: action.productInfo,
									dispatch: action.dispatch,
								}),
							];
						}
						default: {
							return architecture.assertExhaustiveSwitch(action, [
								state,
								reduxLoop.Cmd.none,
							]);
						}
					}
				}
				case "LOAD PRODUCT QUANTITIES": {
					switch (action.status) {
						case "START": {
							const actionIfCmdSucceeds = (
								data: domain.ProductQuantities
							): Action => {
								return {
									type: "INITIALIZATION",
									step: "LOAD PRODUCT QUANTITIES",
									status: "SUCCESS",
									user: action.user,
									config: action.config,
									productInfo: action.productInfo,
									productQuantities: data,
									dispatch: action.dispatch,
								};
							};

							const actionIfCmdFails = (error: string): Action => {
								return {
									type: "POLLING",
									step: "LOAD PRODUCT QUANTITIES",
									status: "FAILURE",
									errorMsg: error,
									dispatch: action.dispatch,
								};
							};

							const startGetQuantities = reduxLoop.Cmd.run(
								persistence.loadQuantities,
								{
									successActionCreator: actionIfCmdSucceeds,
									failActionCreator: actionIfCmdFails,
									args: [action.config],
								}
							);
							return [state, startGetQuantities];
						}
						case "SUCCESS": {
							return [
								state,
								reduxLoop.Cmd.action<Action>({
									type: "INITIALIZATION",
									step: "FINISH",
									user: action.user,
									config: action.config,
									productInfo: action.productInfo,
									productQuantities: action.productQuantities,
									dispatch: action.dispatch,
								}),
							];
						}
						default: {
							return architecture.assertExhaustiveSwitch(action, [
								state,
								reduxLoop.Cmd.none,
							]);
						}
					}
				}
				case "FINISH": {
					const scheduleNextPollAction: () => PollingAction =
						(): PollingAction => {
							return {
								type: "SCHEDULE NEXT POLL",
								user: action.user,
								config: action.config,
								productInfo: action.productInfo,
								dispatch: action.dispatch,
							};
						};

					const newState: Initialized = {
						initialization: "success",
						productInfo: action.productInfo,
						user: action.user,
						config: action.config,
						expandedGroups: immutable.Set(),
						productQuantities: action.productQuantities,
						timerId: Maybe.nothing(),
					};
					const nextCmd: reduxLoop.RunCmd<PollingAction> | reduxLoop.NoneCmd =
						reduxLoop.Cmd.run(cancelNextPoll, {
							successActionCreator: scheduleNextPollAction,
							args: [
								state.initialization === "success"
									? state.timerId
									: Maybe.nothing(),
							],
						});
					return [newState, nextCmd];
				}
				default: {
					return architecture.assertExhaustiveSwitch(action, [
						state,
						reduxLoop.Cmd.none,
					]);
				}
			}
		}
		case "SCHEDULE NEXT POLL": {
			const actionIfCmdSucceeds: (timerId: number) => Action = (
				timerId: number
			): Action => {
				return {
					type: "STORE POLL TIMER ID",
					timerId,
				};
			};
			return [
				state,
				reduxLoop.Cmd.run(scheduleNextPoll, {
					successActionCreator: actionIfCmdSucceeds,
					args: [
						action.dispatch,
						action.config,
						action.user,
						action.productInfo,
					],
				}),
			];
		}
		case "STORE POLL TIMER ID": {
			const newState: State =
				state.initialization === "success"
					? {
							...state,
							timerId: Maybe.just(action.timerId),
					  }
					: state;
			return [newState, reduxLoop.Cmd.none];
		}
		case "POLLING": {
			switch (action.status) {
				case "FAILURE": {
					if (state.initialization === "success") {
						const scheduleNextPollAction: () => PollingAction =
							(): PollingAction => {
								return {
									type: "SCHEDULE NEXT POLL",
									user: state.user,
									config: state.config,
									productInfo: state.productInfo,
									dispatch: action.dispatch,
								};
							};
						const nextCmd: reduxLoop.RunCmd<PollingAction> | reduxLoop.NoneCmd =
							reduxLoop.Cmd.run(cancelNextPoll, {
								successActionCreator: scheduleNextPollAction,
								args: [
									state.initialization === "success"
										? state.timerId
										: Maybe.nothing(),
								],
							});
						return [state, nextCmd];
					} else {
						const reason =
							action.errorMsg === undefined
								? "Unknown reason"
								: action.errorMsg;
						const errorMsg = `Could not load info: ${reason}`;
						const newState: State = {
							errorMsg,
							initialization: "failed",
						};
						return [newState, reduxLoop.Cmd.none];
					}
				}
				default: {
					return [state, reduxLoop.Cmd.none];
				}
			}
		}
		case "LOGOUT": {
			switch (action.status) {
				case "START": {
					const actionIfCmdFails = (error: string): Action => ({
						type: "LOGOUT",
						status: "FAILURE",
						errorMsg: error,
					});

					const actionIfCmdSucceeds = (): Action => {
						return {
							type: "LOGOUT",
							status: "SUCCESS",
							config: action.config,
							dispatch: action.dispatch,
						};
					};
					const logoutCmd: reduxLoop.CmdType = reduxLoop.Cmd.run(
						persistence.logout,
						{
							successActionCreator: actionIfCmdSucceeds,
							failActionCreator: actionIfCmdFails,
							args: [action.config],
						}
					);
					return [state, logoutCmd];
				}
				case "SUCCESS": {
					const newState: Pending = { initialization: "pending" };
					const newActionCmd: reduxLoop.CmdType = reduxLoop.Cmd.action<Action>({
						type: "INITIALIZATION",
						step: "ENSURE VALID SESSION",
						status: "START",
						config: action.config,
						dispatch: action.dispatch,
					});
					return [newState, newActionCmd];
				}
				case "FAILURE": {
					const newState: Failed = {
						initialization: "failed",
						errorMsg: action.errorMsg,
					};
					return [newState, reduxLoop.Cmd.none];
				}
				default: {
					return architecture.assertExhaustiveSwitch(action, [
						state,
						reduxLoop.Cmd.none,
					]);
				}
			}
		}
		case "TOGGLE GROUP EXPANSION": {
			if (state.initialization !== "success") {
				return [state, reduxLoop.Cmd.none];
			} else {
				const newState: State = state.expandedGroups.has(action.group.name)
					? {
							...state,
							expandedGroups: state.expandedGroups.delete(action.group.name),
					  }
					: {
							...state,
							expandedGroups: state.expandedGroups.add(action.group.name),
					  };
				return [newState, reduxLoop.Cmd.none];
			}
		}
		default: {
			return architecture.assertExhaustiveSwitch(action, [
				state,
				reduxLoop.Cmd.none,
			]);
		}
	}
}

// VIEW

interface StateProps {
	state: State;
}

interface HandlerProps {
	dispatch: architecture.Dispatch<Action>;
}

type ViewProps = StateProps & HandlerProps;

interface ModuleRouteProps {
	moduleRoute: string;
}

// eslint-disable-next-line @typescript-eslint/ban-types
function RouteParamsDecoder(data: {}): Either<string, ModuleRouteProps> {
	const decoder = jsonous
		.field("moduleRoute", jsonous.string)
		.andThen((moduleRoute) =>
			jsonous.succeed<ModuleRouteProps>({ moduleRoute: moduleRoute })
		);
	return applyJsonousDecoder(
		decoder,
		{
			failure: (error: string): string => error,
			success: (params: ModuleRouteProps): ModuleRouteProps => params,
		},
		data
	);
}

function scheduleNextPoll(
	dispatch: architecture.Dispatch<Action>,
	config: domain.Config,
	user: domain.User,
	productInfo: domain.ProductInfo
): number {
	return window.setTimeout((): void => {
		dispatch({
			type: "INITIALIZATION",
			step: "LOAD PRODUCT QUANTITIES",
			status: "START",
			config: config,
			user: user,
			productInfo: productInfo,
			dispatch: dispatch,
		});
	}, config.pollingInterval);
}

function cancelNextPoll(timerId: Maybe<number>): void {
	timerId.caseOf({
		just: (id: number): void => {
			clearTimeout(id);
		},

		// eslint-disable-next-line @typescript-eslint/no-empty-function
		nothing: (): void => {},
	});
}

function View({ state, dispatch }: ViewProps): JSX.Element {
	React.useEffect((): void => {
		dispatch({ type: "INITIALIZATION", step: "START", dispatch: dispatch });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	switch (state.initialization) {
		case "success": {
			return (
				<BrowserRouter basename="/app">
					<Switch>
						<Route
							path="/:moduleRoute"
							render={({
								match,
							}: RouteComponentProps<ModuleRouteProps>): JSX.Element => {
								const params = RouteParamsDecoder(match.params);
								return (
									<Homepage
										selectedModule={params.caseOf({
											left: () => Maybe.nothing(),
											right: (validParams) =>
												getMaybeSelectedModule(
													state.config,
													validParams.moduleRoute
												),
										})}
										productInfo={state.productInfo}
										config={state.config}
										user={state.user}
										quantities={state.productQuantities}
										expandedGroups={state.expandedGroups}
										onLogout={(): void => {
											dispatch({
												type: "LOGOUT",
												status: "START",
												config: state.config,
												dispatch: dispatch,
											});
										}}
										onGroupClick={(group: domain.Group): void => {
											dispatch({
												type: "TOGGLE GROUP EXPANSION",
												group,
											});
										}}
									/>
								);
							}}
						/>
						<Route
							path="/"
							render={(): JSX.Element => (
								<Homepage
									selectedModule={Maybe.nothing()}
									productInfo={state.productInfo}
									config={state.config}
									user={state.user}
									quantities={state.productQuantities}
									expandedGroups={state.expandedGroups}
									onLogout={(): void => {
										dispatch({
											type: "LOGOUT",
											status: "START",
											config: state.config,
											dispatch: dispatch,
										});
									}}
									onGroupClick={(group: domain.Group): void => {
										dispatch({
											type: "TOGGLE GROUP EXPANSION",
											group,
										});
									}}
								/>
							)}
						/>
						<Redirect to="/app" />
					</Switch>
				</BrowserRouter>
			);
		}
		case "failed": {
			return <div>Failed loading the app: {state.errorMsg}</div>;
		}
		case "pending": {
			return <div style={styles.loader}>Loading...</div>;
		}
		case "uninitialized": {
			return <div />;
		}
	}
}

function getMaybeSelectedModule(
	config: domain.Config,
	moduleRoute: string
): Maybe<domain.Module> {
	const matchedModule: domain.Module | undefined = config.menu
		.reduce((soFar: domain.Module[], current: domain.Item) => {
			return current.type === "module"
				? [...soFar, current]
				: [...soFar, ...current.modules];
		}, [])
		.find((module: domain.Module): boolean => module.route === moduleRoute);
	return matchedModule !== undefined
		? Maybe.just(matchedModule)
		: Maybe.nothing();
}

function registerAsIframeParent(actionConfig: domain.Config): void {
	const sessionManagementConfig: miimetiqUI.sessionManagement.Config = {
		loginUrl: actionConfig.loginUrl,
		sessionInfoUrl: actionConfig.sessionInfoUrl,
		sessionCookieName: actionConfig.sessionCookieName,
		userManagementUrl: actionConfig.userManagementUrl,
	};
	miimetiqUI.sessionManagement.registerAsIframeParent(sessionManagementConfig);
}

function ensureValidSession(actionConfig: domain.Config): Promise<string> {
	const sessionManagementConfig: miimetiqUI.sessionManagement.Config = {
		loginUrl: actionConfig.loginUrl,
		sessionInfoUrl: actionConfig.sessionInfoUrl,
		sessionCookieName: actionConfig.sessionCookieName,
		userManagementUrl: actionConfig.userManagementUrl,
	};
	return miimetiqUI.sessionManagement.ensureValidSession(
		sessionManagementConfig
	);
}

// REDUX BOILERPLATE

function mapStateToProps(state: State): StateProps {
	return { state: state };
}

function mapDispatchToProps(
	dispatch: architecture.Dispatch<Action>
): HandlerProps {
	return { dispatch: dispatch };
}

const ConnectedView = reactRedux.connect(
	mapStateToProps,
	mapDispatchToProps
)(View);
